import type { ReactNode } from 'react'
import type { DeepPartial, IndicatorStyle, Styles } from 'klinecharts'
import type {
  IndicatorStyleOverrides,
  LegendValueFormat,
  ParamSchema,
} from '@edge/charts-indicators'
import type { DataFeed } from './datafeed'
import type { Period, PeriodKey } from '../utils/resolution'
import type { TimeframeVisibilityRule } from '../utils/timeframeVisibility'
import type { OverlaySyncGroup, OverlaySyncEvent } from '../utils/overlaySync'
import type { ChartSettings, ChartSettingsProfile } from '../components/SettingsModal'
import type { IndicatorEntry, OverlayStyleOverride } from './persistence'
import type { EarningsEvent } from './earnings'
import type { SplitEvent } from './splits'
import type { DilutionLevel } from '../utils/dilutionPriceLine'
import type { PriceAlertLine } from '../utils/priceAlertLine'
import type { AddPriceAlertRequest } from '../utils/crosshairPriceAction'
import type { ChartInteractionSettings } from './interaction'
import type { AnchorVisibleWindow } from '../utils/anchorWindow'
import type { DrawingToolsBundle } from '../components/DrawingToolsMenu'

/** klinecharts' built-in main-pane id. Sub-panes get generated ids
 *  (pane_2, …); the price/candle pane is always this fixed id. */
export const CANDLE_PANE_ID = 'candle_pane'

export interface EdgeSymbol {
  ticker: string
  name?: string
  shortName?: string
  exchange?: string
  type?: 'stock' | 'crypto' | 'forex' | 'futures' | 'index'
  pricePrecision?: number
  volumePrecision?: number
  logo?: string
}

/**
 * Per-bar volume-price histogram — how this bar's volume distributed across its
 * own high–low span, derived server-side from the 1-minute sub-bars it
 * aggregates. Present only on intraday resolutions 5m–240m, and only when the
 * chart asked for it. Absent means "fall back to uniform spread across the
 * bar's range", never "no volume".
 *
 * Bucket `i` spans `low + i*(high-low)/K` → `low + (i+1)*(high-low)/K`, where
 * `K = u.length` — self-describing, because K adapts per bar
 * (`min(24, 2*subBars)`) and shrinks on half-days and gappy names.
 *
 * Spec: docs/superpowers/specs/2026-07-28-volume-profile-intraday-granularity-design.md
 */
export interface BarVolumeHistogram {
  /** Up-volume per price bucket, lowest bucket first. */
  u: number[]
  /** Down-volume per price bucket, lowest bucket first. */
  d: number[]
}

export interface EdgeBar {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume: number
  turnover?: number
  /** Optional finer-than-bar volume distribution — see {@link BarVolumeHistogram}. */
  vp?: BarVolumeHistogram
}

export type ThemeMode = 'dark' | 'light'
export type EdgeTheme = ThemeMode | DeepPartial<Styles>

/** Data-space crosshair position for syncing across charts */
export interface CrosshairSyncData {
  dataIndex: number
  timestamp: number
  value: number
  paneId: string
  /** Sender's symbol — the ticker channel only mirrors same-symbol charts. */
  symbol?: string
  /** Sender is connected to the same-ticker channel (broadcasting on it). */
  tickerSync?: boolean
  /** Sender is connected to the global (cross-ticker, by-time) channel. */
  globalSync?: boolean
}

/**
 * Simple pub/sub for syncing crosshairs across multiple charts.
 *
 * `publish(sourceId, null)` is a "clear" signal — peer subscribers should
 * remove any crosshair line they last drew from this group. Use this when
 * a chart leaves the group (e.g. user toggles "Sync crosshair" off) so the
 * other charts don't keep painting the stale position.
 */
export interface CrosshairSyncGroup {
  subscribe: (id: string, handler: (data: CrosshairSyncData | null) => void) => void
  unsubscribe: (id: string) => void
  publish: (sourceId: string, data: CrosshairSyncData | null) => void
}

/**
 * Visible date range shared across charts in a "Date range" sync group.
 * Timestamps in ms — peers map to their own bars via `setVisibleDateRange`.
 * When peer has no bars in [from, to] the existing setVisibleDateRange falls
 * back to scrolling the nearest bar into view — that 1-2 visible-bar state
 * is the intended "no data here" signal (standard charting behavior).
 */
export interface ViewportSyncData {
  from: number
  to: number
  /**
   * The same range expressed relative to the source chart's anchor (ET
   * minutes plus session offsets). Present only on an anchored group; a peer
   * that has its own anchor replays the window on its own day instead of the
   * absolute range, so charts on different event days scroll together by time
   * of day. A peer without an anchor falls back to `from`/`to`.
   */
  window?: AnchorVisibleWindow
}

export interface ViewportSyncGroup {
  subscribe: (id: string, handler: (data: ViewportSyncData) => void) => void
  unsubscribe: (id: string) => void
  publish: (sourceId: string, data: ViewportSyncData) => void
  /** True for a group whose members sync by time of day on their own anchor day. */
  readonly anchored?: boolean
}

/**
 * A host-managed klinecharts indicator surfaced through EdgeChart's native
 * legend system. The HOST owns the indicator's lifecycle (it called
 * `registerIndicator` + created the instance and knows its pane); EdgeChart
 * renders the legend row — label, crosshair figure values, and the
 * hide / move-pane / settings / remove buttons — and routes every action to
 * the host's callbacks. Used by the backtest chart to give Debug Chart series
 * the same controls as catalog indicators.
 */
export interface ExternalIndicatorEntry {
  /** Unique legend key — must not collide with EdgeChart's own `ind_N` keys. */
  key: string
  /** klinecharts indicator instance name (the host registered + created it). */
  instanceName: string
  /** Legend row label. */
  label: string
  /** Pane the instance is mounted on: 'candle_pane' or a sub-pane id. */
  paneId: string
  visible: boolean
  /** Per-figure CURRENT styles keyed by figure key — colors the legend's
   *  crosshair values and seeds the settings drawer's working copy. */
  styles?: IndicatorStyleOverrides
  /**
   * Figure declarations for the native settings drawer's Style section.
   * `defaultColor` is the host's baseline (e.g. the debug palette slot) that
   * "Reset to defaults" returns to. Together with `onStylesChange` this turns
   * the gear into the standard settings drawer (colors / width / line style).
   */
  figures?: Array<{ key: string; label: string; defaultColor: string }>
  /**
   * Inputs the host owns (a script's `input.*`): with `onCalcParamsChange` they fill the
   * drawer's Inputs tab. `calcParams` holds the current values in `params` order. They
   * open the drawer on their own: a script with inputs and no plots (it only draws
   * lines, labels or boxes, or colors bars) gets the gear and an Inputs-only drawer.
   */
  params?: ParamSchema[]
  calcParams?: unknown[]
  /** Live input edits from the drawer; `undefined` means "reset to the defaults". */
  onCalcParamsChange?: (calcParams: unknown[] | undefined) => void
  /**
   * Live style edits from the settings drawer. Called on every change;
   * `undefined` means "reset to the host's defaults". The host owns applying
   * the styles to its klinecharts instance AND reflecting them back through
   * `styles` on the next render.
   */
  onStylesChange?: (styles: IndicatorStyleOverrides | undefined) => void
  /**
   * Pane placement, used for the move-pane arrow direction ('main' = overlaid
   * on price). The arrow only renders when `onMovePane` is provided.
   */
  placement?: 'main' | 'sub'
  onToggleVisibility?: () => void
  /** Flip between the price-pane overlay and an own sub-pane (host re-mounts). */
  onMovePane?: () => void
  /**
   * Whether this series is "focused" onto the price chart — drives the active
   * (filled/highlighted) state of the focus button. Only meaningful for a
   * sub-pane series that can mirror onto the main price pane.
   */
  isFocused?: boolean
  /**
   * The row is also drawn on the candles while it lives in a lower pane (a backtest
   * Debug Chart condition: its lane stays below, and it also shades the candles).
   * The move arrow then points down ("Show below chart only").
   */
  onCandles?: boolean
  /**
   * Pin/unpin this series onto the main price chart WITHOUT moving it off its
   * own sub-pane (distinct from `onMovePane`, which relocates it). The focus
   * button only renders when this is provided; used by the backtest chart to
   * mirror a debug condition's band onto price. The `key` is passed back so the
   * host can resolve which series toggled.
   */
  onToggleFocus?: (key: string) => void
  /**
   * Custom gear behavior; overrides the native settings drawer. Leave unset
   * and provide `figures` + `onStylesChange` (Style) and/or `params` +
   * `onCalcParamsChange` (Inputs) to get the standard drawer; the gear is
   * hidden when the entry has none of these to edit.
   */
  onOpenSettings?: () => void
  /** Locate button (crosshair icon) — e.g. focus the series' source node in
   *  the host's editor. Hidden when omitted. */
  onLocateSource?: () => void
  /** X click. Omit to hide the remove button (read-only host contexts). */
  onRemove?: () => void
  /** The X's tooltip when it does more than "Remove" (a backtest Debug Chart:
   *  "Delete Debug Chart"). */
  removeTitle?: string
  /** How each figure's legend value prints, by figure key (a script's Pine format,
   *  precision and display); absent keeps the compact readout. */
  valueFormats?: Record<string, LegendValueFormat>
  /**
   * A figure whose color changes per bar (a script plot's series color, Pine v6 manual):
   * its color on the bar opening at `timestamp`, which colors the legend's value at the
   * crosshair; undefined keeps the figure's usual color.
   */
  figureColorAt?: (figureKey: string, timestamp: number) => string | undefined
  /** Lane assignment for a multi-lane "strip" pane. Forwarded onto the legend
   *  row so each lane shows its own legend inside one shared pane. When `visible`
   *  (the ordered per-lane visibility) is set, the legend positions rows with
   *  variable lane heights — hidden lanes collapse to `collapsedPx` — instead of
   *  equal bands, matching the strip's own collapse. */
  lane?: { index: number; count: number; visible?: boolean[]; collapsedPx?: number }
  /**
   * The host is computing this series (a script evaluation in flight): the legend shows a
   * small spinner right after the name. Whatever the series drew before stays on the chart
   * meanwhile. Built-in indicators never set it.
   */
  loading?: boolean
}

export interface EdgeChartProps {
  symbol: EdgeSymbol
  period: Period
  datafeed: DataFeed
  theme?: EdgeTheme
  /** Bumps when the app color theme changes. Forces the color-apply effect to
   *  re-run so theme-derived chart colors repaint on same-mode preset switches
   *  (where the `theme` object identity may otherwise look unchanged). */
  themeVersion?: number
  locale?: string
  timezone?: string
  drawingBarVisible?: boolean
  mainIndicators?: IndicatorEntry[]
  subIndicators?: IndicatorEntry[]
  styles?: DeepPartial<Styles>
  className?: string
  style?: React.CSSProperties
  /** Unique ID for crosshair sync (required if crosshairSync is set) */
  chartId?: string
  /** Shared group for syncing crosshairs across charts */
  crosshairSync?: CrosshairSyncGroup
  /** Connected to the same-ticker crosshair channel (mirror same-symbol charts).
   *  Default true. */
  crosshairTickerSync?: boolean
  /** Connected to the global crosshair channel (mirror ANY chart on it, by time
   *  — the cross-ticker "sympathy" club). Default false. */
  crosshairGlobalSync?: boolean
  /** Shared group for syncing visible date range across charts */
  viewportSync?: ViewportSyncGroup
  /** Shared group for syncing drawing overlays across same-ticker charts */
  overlaySyncGroup?: OverlaySyncGroup
  /** Initial chart type (from workspace store or other source) */
  initialChartType?: string
  /** Initial settings for persistence */
  initialSettings?: Partial<ChartSettings>
  /** Initial main indicators for persistence */
  initialMainIndicators?: IndicatorEntry[]
  /** Initial sub indicators for persistence */
  initialSubIndicators?: IndicatorEntry[]
  /** Called when chart settings change */
  onSettingsChange?: (settings: ChartSettings) => void
  /**
   * Shared chart settings owned by a grid host, fanned out to every cell.
   * `useChartStyles` merges them into this chart's local settings whenever
   * they change, so an edit in the grid's shared SettingsModal repaints every
   * cell live. Keys absent here keep the chart's own value.
   */
  globalSettings?: Partial<ChartSettings>
  /**
   * Per-user global interaction settings (wheel/drag grammar + zoom anchor) —
   * identical across every chart on screen by design. Not merged into
   * `ChartSettings`/`globalSettings`: it never enters panel persistence, so a
   * stale per-panel copy can't diverge from the user's global choice. Defaults
   * to `DEFAULT_INTERACTION_SETTINGS` when omitted.
   */
  interactionSettings?: ChartInteractionSettings
  /**
   * Called with a partial patch when the user edits the Settings modal's
   * "Interaction" section (design D5) — merges into the caller's global
   * store, never into per-panel `ChartSettings`. Omit to no-op (the section
   * still renders; edits just don't go anywhere), which is only correct for
   * a host that hasn't wired global interaction storage yet.
   */
  onInteractionSettingsChange?: (patch: Partial<ChartInteractionSettings>) => void
  /**
   * Saved chart settings profiles to render in the Settings modal. Each
   * profile is a named snapshot of `ChartSettings`; the app supplies the
   * list and handles persistence + payload retrieval.
   */
  settingsProfiles?: ChartSettingsProfile[]
  /** Save the current settings as a new profile with this name. */
  onSaveSettingsProfile?: (name: string) => void
  /** Load a profile's settings into the chart (replaces current settings). */
  onLoadSettingsProfile?: (profileId: string) => void
  /** Delete a profile from the saved list. */
  onDeleteSettingsProfile?: (profileId: string) => void
  /** Called when chart type changes */
  onChartTypeChange?: (chartType: string) => void
  /** Called when indicators change (includes calcParams for accurate restoration) */
  onIndicatorsChange?: (main: IndicatorEntry[], sub: IndicatorEntry[]) => void
  /** Seed for the drawing-bar eye's hidden categories. Applied to the engine
   *  once the chart mounts; later changes flow out via `onHiddenStateChange`
   *  so the host can persist them (they were previously lost on refresh). */
  initialHiddenState?: { drawings?: boolean; indicators?: boolean }
  /** Fired when the drawing-bar eye hides/shows drawings and/or indicators. */
  onHiddenStateChange?: (state: { drawings: boolean; indicators: boolean }) => void
  /** When set, the `s` show/hide-all hotkey delegates to the HOST instead of
   *  toggling this chart alone — the scanner uses it to broadcast the toggle
   *  across every cell. Omitted for standalone charts (they self-toggle). */
  onToggleVisibilityAll?: () => void
  /** Called when a drawing overlay event fires (create/update/remove) */
  onDrawingEvent?: (ticker: string, event: OverlaySyncEvent) => void
  /**
   * Diagnostics for the saved-drawing restore, for host telemetry. Both mean
   * the one-shot chart:ready could not be relied on; neither loses drawings.
   *  - `ready_recovered`: a settings reload superseded the first load and had
   *    to mark the chart ready itself.
   *  - `ready_missed`: bars landed and restored the drawings, and chart:ready
   *    never followed (a path we do not know about yet).
   */
  onRestoreDiagnostic?: (kind: 'ready_recovered' | 'ready_missed', info: { period: string }) => void
  /** Saved drawings to restore from persistence */
  savedDrawings?: Array<{
    syncId: string
    name: string
    points: Array<{ timestamp: number; value: number }>
  }>
  /** Template list to display in toolbar */
  templates?: Array<{ templateId: string; name: string }>
  /** Called when user saves current state as template */
  onSaveTemplate?: (name: string) => void
  /** Called when user loads a template */
  onLoadTemplate?: (templateId: string) => void
  /** Called when user deletes a template */
  onDeleteTemplate?: (templateId: string) => void
  /** Called when user overwrites a saved template with the current chart */
  onUpdateTemplate?: (templateId: string) => void
  /** Called when drawing bar visibility changes */
  onDrawingBarVisibilityChange?: (visible: boolean) => void
  /**
   * Favorited drawing tools (overlay names) for the floating DrawingBar's
   * shortlist, and the star toggle the catalog menu calls. Global per-user
   * state the host owns; the chart only displays and reports.
   */
  drawingFavorites?: string[]
  onToggleDrawingFavorite?: (overlayName: string) => void
  /**
   * Hands the host the same drawing-handler bundle this chart gives its own
   * floating DrawingBar (select / magnet / lock / eye / trash, plus the
   * favorites above), re-published whenever any of it changes, and `null` on
   * unmount. Lets a host with no grid (the plain Chart panel) surface the
   * catalog in its header without rebuilding the handlers.
   */
  onDrawingToolsChange?: (tools: DrawingToolsBundle | null) => void
  /**
   * When set, the Toolbar renders a "Layout" button at its far right (the same
   * one the standalone Toolbar exposes). Used by multi-pane hosts (e.g. the
   * backtest chart grid) to open a layout-picker popover from the chart's own
   * toolbar instead of a bespoke header control.
   */
  onLayout?: (e: React.MouseEvent) => void
  /** Highlights the Toolbar Layout button as active (popover open). */
  layoutActive?: boolean
  /** When set, the Toolbar's right cluster renders a drill-down button
   *  (corner-left-down). Hosts open their day-drill overlay from it. */
  onDrillDown?: () => void
  /**
   * When false, the floating toolbar hides its left control cluster
   * (timeframe / type / ƒx / templates) while keeping the OHLC header and
   * the right tool cluster — used when the workspace panel header hosts
   * those controls instead. Defaults to true.
   */
  toolbarControlsVisible?: boolean
  /**
   * Trim the toolbar's controls to the timeframe alone (no chart type,
   * indicators or templates), for a chart that is read rather than configured.
   * Defaults to false.
   */
  toolbarTimeframeOnly?: boolean
  /**
   * Optional leading content for the Toolbar (far left, before the timeframe
   * button). Lets a host fold the chart's title — e.g. `AAPL · 2024-01-05` —
   * into the toolbar so it serves as the chart's single header.
   */
  toolbarTitle?: ReactNode
  /** When false, hides the Toolbar and DrawingBar — chart fills the freed space */
  toolbarVisible?: boolean
  /**
   * Extra px the top-left overlay stack (OHLC header, legend, pills) shifts
   * down to clear host-rendered floating chrome; only meaningful when the
   * chart's own toolbar is hidden. Defaults to 0.
   */
  overlayTopInset?: number
  /**
   * Content rendered at the start of the top-left OHLC legend line (e.g. a
   * "Linked" chip). Follows the legend's `overlayTopInset` shift, and still
   * renders at the same spot when the OHLC row is hidden.
   */
  overlayLeading?: ReactNode
  /**
   * Opt-in: show the DrawingBar even when `toolbarVisible` is false. Lets a host
   * that hides the top toolbar (e.g. backtest grid cells sharing one toolbar)
   * still surface drawing tools behind its own setting. Defaults to off.
   */
  drawingToolsEnabled?: boolean
  /** Initial focus timestamp — loads historical data ending at this date instead of today */
  initialFocusTimestamp?: number | null
  /**
   * Visible window an anchored chart zooms to (see {@link AnchorVisibleWindow}).
   * Initial value only — change it live through the `setAnchorVisibleWindow`
   * API (mirrors how `initialFocusTimestamp` pairs with `setFocusTimestamp`).
   * Omitted ⇒ the full 4:00–20:00 ET session.
   */
  anchorVisibleWindow?: AnchorVisibleWindow
  /**
   * Shade a fixed [from, to] timestamp span behind the bars (e.g. the Pattern Explorer's
   * open->checkpoint window). `color` is a resolved canvas color (rgba/hsl with alpha). Null/omit
   * clears it. Applied live via the setRangeHighlight bridge.
   */
  rangeHighlight?: {
    from: number
    to: number
    color: string
    lineColor?: string
    label?: string
  } | null
  /**
   * The chart is anchored to a past date (historical mode), not tracking the
   * live edge. Suppresses the current-bar countdown, which is meaningless when
   * there's no forming bar (it would just sit at 0:00). Defaults to false.
   */
  historical?: boolean
  /**
   * Horizontal reference line at TODAY's live price for anchored/historical
   * charts (full-width dashed line + y-axis pill, optional "Today"-style tag).
   * Prop-driven: a price change moves the line in place; null/undefined
   * removes it. See `EdgeChartApi.setReferencePriceLine` for the imperative
   * equivalent.
   */
  referencePrice?: { price: number; label?: string } | null
  /**
   * The session's extended-hours price for the Pre/Post price mark ("Pre" before
   * the open, "Post" after the close, held overnight). Drawn only on a live daily
   * chart; the chart's own premarket stream refreshes a "Pre" value between host
   * updates. Null clears it.
   */
  extendedSessionPrice?: { label: 'Pre' | 'Post'; price: number } | null
  /** Optional second line rendered below the "TICKER - Timeframe" watermark
   * (e.g. an event date). Faint, centered, inherits the watermark styling. */
  watermarkSubtitle?: string
  /** Show the small Edge brand mark in the bottom-left corner. Default true.
   *  A multi-chart grid passes `false` on every cell except the bottom-left one,
   *  so the whole grid carries a single mark instead of one per cell. */
  showWatermark?: boolean
  /** Initial bar space (zoom level) — restores previous zoom on remount */
  initialBarSpace?: number
  /** Background-prefetch the coarse preload period(s). Defaults to true; grid /
   *  scanner / history cells pass false (see ChartManagerOptions.enablePreload). */
  enablePreload?: boolean
  onReady?: () => void
  onSymbolChange?: (symbol: EdgeSymbol) => void
  onPeriodChange?: (period: Period) => void
  /**
   * The visible window changed (scroll, zoom, a programmatic range): the first
   * and last visible bars' timestamps. Throttled to one call per frame.
   */
  onVisibleRangeChange?: (range: ViewportSyncData) => void
  /**
   * Bar replay moved: the timestamp of the last bar now shown (on start and
   * on every step). Lets a host know where a replay stands, e.g. to mark the
   * moment the learner reacted.
   */
  onReplayStep?: (cursorTs: number) => void
  /**
   * User's favorite timeframes (quick-access style). When non-empty they render
   * inline in the toolbar with a "more" chevron for the rest; when empty the
   * timeframe control stays the single-button dropdown. Global per-user — the
   * host owns persistence and passes the same list to every chart.
   */
  favoriteTimeframes?: PeriodKey[]
  /**
   * Toggle a timeframe in/out of favorites (the star buttons in the timeframe
   * dropdown). When omitted, the favorites UI is disabled entirely.
   */
  onToggleFavoriteTimeframe?: (key: PeriodKey) => void
  /**
   * Controlled state for the price-pane indicator-legend minimize chevron. When
   * `onToggleLegendCollapse` is provided the chart is controlled (a host can
   * share one collapse state across a grid of charts); otherwise the chart owns
   * an internal, ephemeral collapse state.
   */
  legendCollapsed?: boolean
  onToggleLegendCollapse?: () => void
  /**
   * Fired when the user shift+clicks a bar on a daily timeframe. The payload
   * is the clicked trading day (midnight-UTC aligned). The host should jump
   * the chart to that day's intraday view — e.g. set its focus date and switch
   * to a 1-minute period — so the whole panel reloads, not just one chart.
   */
  onIntradayDrill?: (dayTimestamp: number) => void
  /**
   * Body context-menu → "Replay from this day". The payload is the trading day
   * (midnight-UTC aligned) of the bar under the right-click. Opt-in: when
   * omitted, the row doesn't render. The host resolves the day's session open
   * and enters bar replay (see chart-grid `handleReplayPick`).
   */
  onReplayFromDay?: (dayTimestamp: number) => void
  /**
   * Body context-menu → "Ask AI about this day". The payload is the trading day
   * (midnight-UTC aligned) of the bar under the right-click. Opt-in: when
   * omitted, the row doesn't render. The host opens the AI Companion seeded with
   * the ticker + day. Kept as a host callback so the engine never imports app
   * stores.
   */
  onAskAiAboutDay?: (dayTimestamp: number) => void
  /**
   * Body context-menu → "Find similar pattern". Payload: the trading day (midnight-UTC aligned) of
   * the bar under the right-click, and the chosen `scopeId` (peer universe — see
   * `findSimilarScopes`; '' when no scope list is provided). Opt-in: when omitted, the row doesn't
   * render. The host seeds the Pattern Explorer with the ticker + day (+ a window) and maps the
   * scope to a peer cohort. Host callback so the engine stays pattern-agnostic.
   */
  onFindSimilarPattern?: (dayTimestamp: number, scopeId: string) => void
  /**
   * Scope options for "Find similar pattern" as a grouped list of section headers + clickable items
   * (e.g. a "Compare To" header, "Same Ticker", a "History Presets" header, then presets). When
   * present, the menu row becomes a submenu; the host maps the picked `scopeId` to a cohort.
   */
  findSimilarScopes?: Array<{ header: string } | { id: string; label: string }>
  /**
   * Body context-menu → "Tag this day…". Opt-in: when omitted, the row doesn't
   * render. The host opens its own tag picker, so the engine never imports the
   * tag feature.
   *
   * NOTE the payload differs from the siblings above: this is the bar's RAW
   * timestamp, NOT the midnight-UTC-aligned trading day. The host converts it to
   * a calendar date using the chart's configured timezone (the same one fed to
   * `setTimezone`) so the tag matches the date rendered on the axis. Passing the
   * aligned `dayTimestamp` here would roll any post-20:00 ET bar to the next day.
   */
  onTagDay?: (barTimestamp: number) => void
  /**
   * Body context-menu → "Exclude {ticker}". Not day-scoped (renders regardless
   * of bar context) and not tied to `onIntradayDrill`'s daily-only gate — it
   * targets the whole ticker, not a specific trading day. Opt-in: when
   * omitted, the row doesn't render. A scanner-style host (Chart Scanner)
   * wires this to drop the ticker from its scan; a single, ungridded chart
   * should leave it omitted.
   */
  onExcludeTicker?: () => void
  /** Host-owned chart-body context-menu toggle for generated transaction traces. */
  autoTracesEnabled?: boolean
  onToggleAutoTraces?: () => void
  /**
   * Controlled maximize state (in-mosaic overlay — NOT browser fullscreen).
   * When `onMaximize` is provided, the host controls isMaximized and EdgeChart
   * delegates all toggle / Escape logic through this callback.
   * When omitted, EdgeChart manages an internal isMaximized state.
   */
  onMaximize?: (next: boolean) => void
  /** Controlled maximized state — only meaningful when `onMaximize` is provided. */
  isMaximized?: boolean
  /**
   * Called when the user clicks "Apply to all" in the indicator settings drawer.
   * Receives the indicator name + current calcParams + style overrides, which
   * the caller (typically a multi-chart panel) broadcasts to sibling cells.
   * When undefined, the "Apply to all" button is hidden in the drawer.
   */
  onApplyIndicatorToAllCharts?: (
    name: string,
    patch: {
      calcParams?: unknown[]
      styles?: IndicatorStyleOverrides
      timeframeVisibility?: TimeframeVisibilityRule
    },
  ) => void
  /**
   * Host-managed indicators to surface through the native legend (rows, hover
   * values, hide/move/settings/remove controls). EdgeChart does NOT create or
   * persist these — the host owns their klinecharts lifecycle and receives
   * every button action via the entry's callbacks.
   */
  externalIndicators?: ExternalIndicatorEntry[]
  /**
   * Earnings events for this symbol. When supplied (and `settings.showEarnings`
   * is on), each event renders as an "E" badge at the bottom of the price pane
   * (beat/miss colored) via the EARNINGS_MARKERS system indicator, with a hover
   * tooltip. The host owns fetching (e.g. chart-panel's
   * `useGetHistoryEarningsQuery`) and maps its rows to `EarningsEvent`;
   * charts-core does no fetching. Omit for charts with no earnings overlay.
   */
  earnings?: EarningsEvent[]
  /**
   * In-play warrant exercise prices AND/OR live S-3 offering prices to draw as
   * read-only "dilution level" lines (one locked dashed horizontal + y-axis
   * pill each). Each level's `kind` ('warrant' default, or 'offering') gates it
   * independently (`showDilutionLevels` vs `showOfferingLevels`) and colors
   * it distinctly. The host owns fetching + selection (chart-panel's lean
   * warrants/offerings queries → `selectDilutionLevels` / `selectOfferingLevels`,
   * concatenated into one array) and passes the resolved levels; charts-core
   * does no fetching. Omit / `[]` for charts with no dilution overlay.
   */
  dilutionLevels?: DilutionLevel[]
  /**
   * Split events for this symbol. When supplied (and `settings.showSplits` is
   * on), each event renders as an "S" badge + compact ratio label at the bottom
   * of the price pane via the SPLIT_MARKERS system indicator. The host owns
   * fetching (chart-panel's chart-metadata query) and maps its rows to
   * `SplitEvent`; charts-core does no fetching. Omit for charts with no split
   * overlay.
   */
  splits?: SplitEvent[]
  /**
   * The user's active price alerts for this chart's ticker, drawn as alert lines: a dashed
   * line and a bell label on the price axis (utils/priceAlertLine.ts). Data lines, not
   * drawings: never persisted or synced, and the drawing-bar eye leaves them alone;
   * `settings.showPriceAlertLines` hides them. The host owns fetching and passes them on
   * live charts only. Omit for charts without price alerts.
   */
  priceAlertLines?: PriceAlertLine[]
  /**
   * The "+" joined to the crosshair price label and the "Add alert at $X" row of both
   * right-click menus. The host opens its price alert popover. Omit to hide all three.
   */
  onAddPriceAlert?: (request: AddPriceAlertRequest) => void
  /** A right click, a double click or a long press on an alert line or its label. Omit and
   *  the alerts take no clicks. */
  onCancelPriceAlert?: (alertId: string) => void
  /** Chart settings show the "Alert lines" switch (useChartSettingsModalProps decides). */
  priceAlertsAvailable?: boolean
  /**
   * Fired when a bar replay starts (`true`) or ends (`false`). Hosts use it to
   * publish the transient `replayOpen` panel-mode flag that frames the whole
   * mosaic window with the orange replay border. Ephemeral — nothing persisted.
   */
  onReplayActiveChange?: (active: boolean) => void
}

/** Magnet mode for drawing tools — see `EdgeChartApi.setOverlayMode`. */
export type OverlayModeName = 'normal' | 'weak_magnet' | 'strong_magnet'

export interface EdgeChartApi {
  /** Loaded bars in ascending timestamp order. */
  getLoadedBars(): ReadonlyArray<{ timestamp: number }>
  /**
   * Swaps the CHART only. A host panel that owns the ticker (every workspace
   * chart does) must change it through the workspace instead, or the panel
   * header and the drawings store keep the old ticker while the chart shows
   * the new one.
   */
  setSymbol(symbol: EdgeSymbol): void
  /** The symbol the chart is showing (or swapping to). */
  getSymbol(): EdgeSymbol
  /** True once the chart shows `ticker` with bars on screen; false on timeout. */
  whenSymbolLoaded(ticker: string, timeoutMs?: number): Promise<boolean>
  /**
   * Apply a drawing change that did NOT originate on this chart (a peer cell, a
   * restore, an undo). Treated as a sync echo: nothing is published back, so the
   * host can call it without the change being persisted a second time.
   */
  applyOverlaySync(event: OverlaySyncEvent): void
  setPeriod(period: Period): void
  setTheme(theme: EdgeTheme): void
  setTimezone(timezone: string): void
  /**
   * Raw timestamp of the bar under the crosshair, or null when the cursor isn't
   * over one. Already public on ChartManager and reachable through EdgeChart's
   * forwarding handle; declared here so hosts can read it type-safely (the tag
   * quick-add resolves its date from this).
   */
  cursorBarTimestamp(): number | null
  /**
   * Apply a full ChartSettings snapshot in place (no remount). Used to
   * load a saved settings profile — pushes the bundle through the same
   * `setSettings` state path that individual modal edits use, so
   * `useChartStyles` issues a single `chart.setStyles({...})` call.
   */
  applySettings(settings: ChartSettings): void
  createIndicator(name: string, isStack?: boolean, paneId?: string): string | null
  removeIndicator(paneId: string, name?: string): void
  updateIndicator(
    paneId: string,
    name: string,
    patch: {
      calcParams?: unknown[]
      styles?: Partial<IndicatorStyle>
      visible?: boolean
      precision?: number
      shortName?: string
    },
  ): boolean
  /**
   * Arm a drawing tool — the entry point an EXTERNAL drawing bar should use.
   *
   * Unlike `createOverlay` (a thin passthrough to ChartManager), this mirrors
   * the chart's own drawing bar exactly: applies the per-tool default style,
   * routes `longPositionTool` / `shortPositionTool` through
   * `createPositionOverlay` (they need expanding to 4 anchors, and a plain
   * `createOverlay` leaves them as a single dead point), and arms the
   * active-tool highlight. Returns the pending overlay id.
   */
  selectTool(name: string): string | null
  /**
   * Cancel the drawing currently being placed, if any. Unlike
   * `removeOverlay(pendingId)` this also DISARMS the tool — it fires the
   * tool-deactivated callback that clears the active-tool highlight and the
   * chart's in-progress state. Removing the overlay alone leaves the cell
   * armed and waiting for clicks.
   */
  cancelPendingDraw(): boolean
  /**
   * Subscribe to the COMMITTED points of a drawing in progress on this chart
   * (one call per click, not per mouse move). Lets a host fan a partly-drawn
   * shape out to sibling charts so it can be finished on any of them.
   */
  setDrawProgressCallback(
    cb: ((points: Array<{ timestamp?: number; value?: number }>) => void) | null,
  ): void
  /**
   * Subscribe to a user-drawn tool's draw-end (geometry complete), including a
   * blank annotation whose create is deferred until its text is committed.
   * Not fired for `measure` or programmatic creates. Normal tools also reach the
   * host's create-sync path afterwards, so host handlers must be idempotent.
   */
  setDrawEndCallback?(cb: (() => void) | null): void
  /**
   * Adopt the committed points of a drawing being made on a sibling chart.
   * Partial point sets only — completion stays with whichever chart receives
   * the final click, and nothing is persisted until then.
   */
  applyPendingPoints(points: Array<{ timestamp?: number; value?: number }>): void
  createOverlay(name: string, initialStyle?: OverlayStyleOverride): string | null
  /**
   * Locked, unpersisted marker at explicit points — annotation overlays only
   * (e.g. an earnings/news chart's fixed callouts). Use `placeOverlayAtPoints`
   * instead for anything the user should be able to edit or that should
   * survive a reload.
   */
  createOverlayAtPoints(
    name: string,
    points: Array<{ timestamp: number; value?: number }>,
    styles?: OverlayStyleOverride,
    extendData?: unknown,
  ): string | null
  /**
   * Unlocked, persisted overlay at explicit points — for a caller (e.g. the
   * Trade Planner) pushing a fully-formed drawing onto the chart that the
   * user can then edit and that survives reload. For `longPositionTool` /
   * `shortPositionTool`, `points` must be the four anchors
   * `expandPositionOverlay` seeds, in order: entry-left, entry-right,
   * target, stop (entry is a horizontal segment, not a single point). Use
   * `createOverlayAtPoints` instead for locked annotation markers.
   */
  placeOverlayAtPoints(
    name: string,
    points: Array<{ timestamp?: number; barOffset?: number; value: number }>,
    opts?: { styles?: OverlayStyleOverride; extendData?: unknown; paneId?: string },
  ): string | null
  removeOverlay(id?: string): void
  overrideOverlay(id: string, props: { lock?: boolean; visible?: boolean; mode?: string }): void
  /** Apply a style override (color / width / line-style) to a live overlay + sync. */
  overrideOverlayStyle(id: string, style: OverlayStyleOverride): void
  /** Move a live overlay's points to new coordinate values + sync. */
  overrideOverlayPoints(id: string, points: Array<{ timestamp: number; value: number }>): void
  /** Show/hide all overlays at once (drawing-bar eye toggle). */
  /** Lock / unlock every overlay at once (drawing bar's "Lock All"). */
  setOverlaysLocked(locked: boolean): void
  /**
   * Magnet mode — snap drawing points to the hovered bar's OHLC. Applies to
   * existing overlays and to every one created afterwards.
   */
  setOverlayMode(mode: OverlayModeName): void
  setOverlaysVisible(visible: boolean): void
  /** Show/hide all indicators at once (drawing-bar eye toggle). */
  setIndicatorsVisible(visible: boolean): void
  /**
   * A grid's shared eye was cleared by an indicator turned on in ANOTHER cell:
   * if this chart's indicators are hidden, lift the hide and switch every
   * price-pane indicator individually off. No-op when not hidden.
   */
  releaseIndicatorHide(): void
  /**
   * Enable/configure read-only pattern detection (S/R zones, etc.). Opt-in —
   * nothing draws until `{ enabled: true }`. `minConfidence` is the sensitivity
   * gate; `maxN` the per-group curation budget. See patternDetection.ts.
   */
  setPatternDetection(config: {
    enabled?: boolean
    minConfidence?: number
    maxN?: number
    priceTolerance?: number
  }): void
  /**
   * Enable/configure the read-only auto Fibonacci (Beta). Anchors a locked
   * retracement to the most recent swing of at least `swingMult` ATRs. Opt-in;
   * nothing draws until `{ enabled: true }`. See autoFib.ts.
   */
  setAutoFib(config: { enabled?: boolean; swingMult?: number }): void
  /**
   * Enable/configure auto trendlines (Beta): lines fitted through the ZigZag
   * swing highs / lows, at most `maxPerDirection` each way, drawn in the same
   * read-only layer as S/R and gated by the same confidence slider.
   */
  setAutoTrendlines(config: { enabled?: boolean; maxPerDirection?: number }): void
  /** Wicks or candle bodies as the touch series for EVERY auto-analysis
   *  detector (S/R, auto Fib, trendlines). Recomputes all of them. */
  setAutoAnalysisTouchInput(input: 'wicks' | 'bodies'): void
  exportImage(type?: string): string
  setFocusTimestamp(timestamp: number | null): void
  /**
   * Show/move/remove the programmatic reference price line (dashed horizontal
   * at `ref.price` + y-axis pill). Null removes it. Chart chrome, not a user
   * drawing — never persisted to `SavedDrawing[]`.
   */
  setReferencePriceLine(ref: { price: number; label?: string } | null): void
  /**
   * Run `cb` every time a batch of bars lands on the chart: the first load,
   * a symbol or period swap, a scroll-back page. If bars are already on
   * screen, `cb` also runs once right away. This is the seam for a host
   * placing timestamp-anchored annotations (a backtest's trade fills): an
   * overlay created before its bars exist has nothing to resolve against.
   * Returns the unsubscribe.
   */
  onBarsLoaded(cb: () => void): () => void
  /**
   * Enter bar replay anchored at `startTs` (future bars withheld, live feed
   * off). Delegates to the ChartManager's lazily-created ReplayController.
   */
  startReplay(startTs: number): Promise<void>
  /**
   * Step an active replay forward by one sub-bar (the replay builds each bar
   * from finer ones). Resolves false when nothing is ahead. A host stepping
   * a whole bar calls it until `onReplayStep` reports the bar complete.
   */
  stepReplay(): Promise<boolean>
  /** Exit bar replay → back to live (resubscribe + reload to now). */
  exitReplay(): void
  /** Zoom the visible area to show bars between two timestamps. */
  setVisibleDateRange(from: number, to: number): void
  /**
   * Visible-range changes (scroll, zoom), at most one call per frame. Optional so test
   * doubles need not carry it.
   */
  subscribeViewport?(callback: (data: ViewportSyncData) => void): void
  /**
   * Removes ONE viewport callback. Always pass it: the manager's no-argument form drops
   * every handler, the chart's own viewport sync included.
   */
  unsubscribeViewport?(callback: (data: ViewportSyncData) => void): void
  /** Open times of the leftmost and rightmost visible bars; null before bars load. */
  getVisibleTimeRange?(): { from: number; to: number } | null
  /**
   * Override the visible window anchored (historical) charts zoom to. Applies
   * live: an already-anchored chart re-zooms immediately. No-op visual effect on
   * a non-anchored (live) chart, which never calls the anchored zoom path.
   */
  setAnchorVisibleWindow(window: AnchorVisibleWindow): void
  scrollToRealTime(): void
  scrollToTimestamp(timestamp: number, animationDuration?: number): void
  getBarSpace(): number
  /**
   * Set a pane's pixel height (and optional minimum). Thin pass-through to
   * klinecharts `setPaneOptions`; used by hosts that size a sub-pane to its
   * content — e.g. a debug condition's own-pane band shrinking to a compact
   * band-row height.
   */
  setPaneHeight(paneId: string, height: number, minHeight?: number): void
  resize(): void
  destroy(): void
}
